import { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Building2,
  Check,
  Code2,
  Copy,
  Eye,
  Download,
  Palette,
  Power,
  QrCode,
  Trash2,
} from "lucide-react";
import { api } from "../../lib/api";

interface WidgetDetailResponse {
  widget: {
    id: string;
    company_id: string;
    name: string;
    service_ids: string[];
    primary_color: string | null;
    tracking_page_url: string | null;
    active: boolean;
    created_at: string;
    updated_at: string;
  };
  company: {
    id: string;
    slug: string;
    name: string;
    primary_color: string;
    tracking_page_url: string | null;
    services: Array<{ id: string; label: string }>;
  };
}

export function WidgetDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["widget", id],
    queryFn: () => api<WidgetDetailResponse>(`/master/widgets/${id}`),
  });

  const { data: codeResp } = useQuery({
    queryKey: ["widget-code", id],
    queryFn: () => api<{ code: string }>(`/master/widgets/${id}/code`),
    enabled: !!data,
  });

  const [name, setName] = useState("");
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [allServices, setAllServices] = useState(false);
  const [primaryColor, setPrimaryColor] = useState("");
  const [trackingPageUrl, setTrackingPageUrl] = useState("");
  const [active, setActive] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!data) return;
    setName(data.widget.name);
    setServiceIds(data.widget.service_ids);
    setAllServices(data.widget.service_ids.length === 0);
    setPrimaryColor(data.widget.primary_color ?? "");
    setTrackingPageUrl(data.widget.tracking_page_url ?? "");
    setActive(data.widget.active);
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api(`/master/widgets/${id}`, {
        method: "PUT",
        body: {
          name,
          serviceIds: allServices ? [] : serviceIds,
          primaryColor: primaryColor || null,
          trackingPageUrl: trackingPageUrl || null,
          active,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["widget", id] });
      qc.invalidateQueries({ queryKey: ["widget-code", id] });
      qc.invalidateQueries({ queryKey: ["widgets"] });
    },
  });

  const deleteWidget = useMutation({
    mutationFn: () => api(`/master/widgets/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["widgets"] });
      navigate("/master/widgets");
    },
  });

  const previewDoc = useMemo(() => {
    if (!codeResp?.code) return "";
    return `<!doctype html>
<html lang="de"><head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
  html,body { margin:0; padding:0; background:#f1f5f9; }
  body { padding:24px 16px; font-family:'Inter','DM Sans',system-ui,sans-serif; }
</style>
</head><body>
${codeResp.code}
</body></html>`;
  }, [codeResp]);

  async function copy() {
    if (!codeResp?.code) return;
    await navigator.clipboard.writeText(codeResp.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function toggle(sid: string) {
    setServiceIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid],
    );
    setAllServices(false);
  }

  if (isLoading) return <p className="text-slate-500">Lädt…</p>;
  if (error || !data)
    return (
      <p>
        Nicht gefunden.{" "}
        <Link to="/master/widgets" className="text-brand hover:underline">
          ← zurück
        </Link>
      </p>
    );

  return (
    <div className="mx-auto max-w-7xl">
      <Link
        to="/master/widgets"
        className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> Widgets
      </Link>

      <header className="mb-5 flex flex-wrap items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100">
          <Code2 className="h-5 w-5 text-slate-600" />
        </div>
        <div>
          <h1 className="page-title">{data.widget.name}</h1>
          <p className="inline-flex items-center gap-1 text-sm text-slate-500">
            <Building2 className="h-3 w-3" />
            {data.company.name}
            <span className="mx-1 text-slate-300">·</span>
            <span className="font-mono text-xs">/{data.company.slug}</span>
          </p>
        </div>
        <span className="ml-auto pill bg-slate-100 text-slate-600 ring-1 ring-slate-200">
          <Power className="h-3 w-3" /> {active ? "Aktiv" : "Inaktiv"}
        </span>
      </header>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Settings */}
        <div className="space-y-5">
          <section className="card">
            <h2 className="section-title mb-4">
              <Code2 className="h-3.5 w-3.5 text-slate-400" /> Konfiguration
            </h2>
            <div className="space-y-3">
              <Row label="Bezeichnung">
                <input
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Row>
              <Row label="Aktiv">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand/30"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
              </Row>
            </div>
          </section>

          <section className="card">
            <h2 className="section-title mb-4">
              <Building2 className="h-3.5 w-3.5 text-slate-400" /> Services
            </h2>
            <label className="mb-3 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand/30"
                checked={allServices}
                onChange={(e) => {
                  setAllServices(e.target.checked);
                  if (e.target.checked)
                    setServiceIds(data.company.services.map((s) => s.id));
                }}
              />
              <span className="font-medium">Alle Services anzeigen</span>
            </label>
            {!allServices && (
              <div className="space-y-2">
                {data.company.services.map((s) => (
                  <label
                    key={s.id}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition ${
                      serviceIds.includes(s.id)
                        ? "border-brand bg-brand/5"
                        : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand/30"
                      checked={serviceIds.includes(s.id)}
                      onChange={() => toggle(s.id)}
                    />
                    <span className="text-sm font-medium text-slate-800">
                      {s.label}
                    </span>
                  </label>
                ))}
                <p className="text-xs text-slate-500">
                  {serviceIds.length} von {data.company.services.length}{" "}
                  ausgewählt
                </p>
              </div>
            )}
          </section>

          <section className="card">
            <h2 className="section-title mb-4">
              <Palette className="h-3.5 w-3.5 text-slate-400" /> Optionale
              Anpassungen
            </h2>
            <div className="space-y-3">
              <Row label="Farbe überschreiben">
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    className="h-10 w-14 cursor-pointer rounded-lg border border-slate-200"
                    value={primaryColor || data.company.primary_color}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                  />
                  <span className="font-mono text-sm text-slate-600">
                    {primaryColor || "Standard"}
                  </span>
                  {primaryColor && (
                    <button
                      type="button"
                      className="text-xs text-slate-500 hover:underline"
                      onClick={() => setPrimaryColor("")}
                    >
                      zurücksetzen
                    </button>
                  )}
                </div>
              </Row>
              <Row label="Tracking-Seite">
                <input
                  className="input"
                  placeholder={
                    data.company.tracking_page_url ?? "Standard verwenden"
                  }
                  value={trackingPageUrl}
                  onChange={(e) => setTrackingPageUrl(e.target.value)}
                />
              </Row>
            </div>

            <div className="mt-5 flex items-center gap-3">
              <button
                className="btn-primary"
                onClick={() => save.mutate()}
                disabled={save.isPending}
              >
                Speichern
              </button>
              {save.isSuccess && (
                <span className="inline-flex items-center gap-1 text-sm text-emerald-600">
                  <Check className="h-4 w-4" /> Gespeichert
                </span>
              )}
            </div>
          </section>

          <section className="card border-red-200 bg-red-50">
            <h2 className="section-title mb-3 text-red-700">
              <AlertTriangle className="h-3.5 w-3.5" /> Gefahrenbereich
            </h2>
            <p className="mb-4 text-sm text-red-700">
              Das Widget wird gelöscht. Bereits eingebetteter Code auf
              Kundenwebsites wird dann nicht mehr funktionieren.
            </p>
            <button
              className="btn-danger btn-sm"
              onClick={() => {
                if (confirm("Widget wirklich löschen?")) deleteWidget.mutate();
              }}
              disabled={deleteWidget.isPending}
            >
              <Trash2 className="h-3.5 w-3.5" /> Widget löschen
            </button>
          </section>
        </div>

        {/* Code + preview */}
        <div className="space-y-5">
          <section className="card">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="section-title">
                <Code2 className="h-3.5 w-3.5 text-slate-400" /> Embed-Code
              </h2>
              <button
                className="btn-secondary btn-sm"
                onClick={copy}
                disabled={!codeResp?.code}
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5" /> Kopiert
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" /> Kopieren
                  </>
                )}
              </button>
            </div>
            {codeResp ? (
              <pre className="max-h-[400px] overflow-auto rounded-xl bg-slate-900 p-4 font-mono text-xs leading-relaxed text-slate-100">
                {codeResp.code}
              </pre>
            ) : (
              <p className="text-sm text-slate-500">Lädt…</p>
            )}
          </section>

          <QrSection
            targetUrl={
              trackingPageUrl ||
              data.widget.tracking_page_url ||
              data.company.tracking_page_url
            }
            widgetName={data.widget.name}
            companyName={data.company.name}
          />

          <section className="card">
            <h2 className="section-title mb-3">
              <Eye className="h-3.5 w-3.5 text-slate-400" /> Vorschau
            </h2>
            {previewDoc ? (
              <iframe
                key={codeResp?.code}
                title="Widget-Vorschau"
                srcDoc={previewDoc}
                className="h-[600px] w-full rounded-xl border border-slate-200 bg-slate-100"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              />
            ) : (
              <p className="text-sm text-slate-500">Lädt…</p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function QrSection({
  targetUrl,
  widgetName,
  companyName,
}: {
  targetUrl: string | null;
  widgetName: string;
  companyName: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!targetUrl || !canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, targetUrl, {
      width: 220,
      margin: 2,
      errorCorrectionLevel: "M",
      color: { dark: "#1a1a2e", light: "#ffffff" },
    }).catch(() => {
      /* canvas render failed — section shows the hint text below */
    });
  }, [targetUrl]);

  function download() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `qr-${companyName}-${widgetName}.png`
      .toLowerCase()
      .replace(/[^a-z0-9.-]+/g, "-");
    a.click();
  }

  return (
    <section className="card">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="section-title">
          <QrCode className="h-3.5 w-3.5 text-slate-400" /> QR-Code
        </h2>
        {targetUrl && (
          <button className="btn-secondary btn-sm" onClick={download}>
            <Download className="h-3.5 w-3.5" /> PNG herunterladen
          </button>
        )}
      </div>

      {targetUrl ? (
        <div className="flex items-center gap-5">
          <canvas
            ref={canvasRef}
            className="rounded-lg border border-slate-200"
          />
          <div className="text-sm text-slate-600">
            <p>
              Führt direkt zum Anfrage-Formular:
              <br />
              <a
                href={targetUrl}
                target="_blank"
                rel="noreferrer"
                className="break-all font-mono text-xs text-brand hover:underline"
              >
                {targetUrl}
              </a>
            </p>
            <p className="mt-3 text-xs text-slate-500">
              Für Fahrzeuge, Flyer, Rechnungen und Visitenkarten. Am besten
              mindestens 2×2 cm drucken.
            </p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-500">
          Hinterlegen Sie zuerst eine Tracking-Seite (die Seite, auf der das
          Widget eingebaut ist) — der QR-Code führt Kunden dorthin.
        </p>
      )}
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-2 sm:grid-cols-[180px_1fr] sm:items-center">
      <span className="text-sm text-slate-600">{label}</span>
      <div>{children}</div>
    </label>
  );
}
